import { useEffect, useState } from 'react'
import { useSessionSelector } from '@/state/session/context'

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

function readSystemReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(REDUCED_MOTION_QUERY).matches
}

function useSystemReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => readSystemReducedMotion())

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(REDUCED_MOTION_QUERY)
    const update = () => setReduced(mql.matches)
    update()
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', update)
      return () => mql.removeEventListener('change', update)
    }
    mql.addListener(update)
    return () => mql.removeListener(update)
  }, [])

  return reduced
}

/**
 * Combines the OS reduced-motion preference with the in-app accessibility setting.
 * Implemented with matchMedia to keep framer-motion out of the initial bundle —
 * before, useReducedMotion from framer-motion forced the lib into every consumer.
 */
export function useShouldAnimate() {
  const systemReduceMotion = useSystemReducedMotion()
  // HAB-142: subscribe to just the reduceMotion flag so this widely-used hook
  // re-renders only when that boolean flips — not on every session mutation.
  const reduceMotion = useSessionSelector((session) => session.accessibility?.reduceMotion ?? false)

  return !(systemReduceMotion || reduceMotion)
}
